/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import { connect } from 'react-redux';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';

import { REMOTE_CATEGORIES } from 'components/zpFilter/filterUtils';

import AutosuggestProxy from '../../services/autosuggestProxy';
import { trackUserAutosuggestActivities } from '../../services/userService';
import {
  updateSearchForParticularDepartment,
  updateSearchWithoutCallNumber,
} from '../../store/searchFilter/actions';
import { generateRandomKey, isUserOutsideUS } from '../../utils';

import css from './autosuggestInput.module.css';

const UNITED_STATES = 'United States';

const inputList = {
  jobTitle: {
    category: 'jobTitle',
    placeholder: 'Job Title',
    errorMessage: "Sorry, we can't find that job title. Please try another.",
    displayValue: '',
    value: '',
    valueObj: {},
  },
  location: {
    category: 'location',
    placeholder: 'Location',
    errorMessage: "Sorry, we can't find that location. Please try another.",
    displayValue: '',
    value: '',
    valueObj: {
      city: '',
      state: '',
    },
  },
  locationExtended: {
    category: 'locationExtended',
    placeholder: 'Location',
    errorMessage: "Sorry, we can't find that location. Please try another.",
    displayValue: '',
    value: '',
    valueObj: {
      city: '',
      state: '',
    },
  },
  major: {
    category: 'major',
    placeholder: 'College Major',
    errorMessage: "Sorry, we can't find that major. Please try another.",
    displayValue: '',
    value: '',
    valueObj: {},
  },
  company: {
    category: 'company',
    placeholder: 'Company',
    errorMessage: "Sorry, we can't find that company. Please try another.",
    displayValue: '',
    value: '',
    valueObj: {},
  },
  skills: {
    placeholder: 'Enter skill: e.g. Javascript',
  },
  certification: {
    placeholder: 'Certification',
  },
  companyIndustry: {
    placeholder: 'Industry',
  },
};

const propTypes = {
  fromHomePage: PropTypes.bool,
  onErrorCallback: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onEnterKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  category: PropTypes.oneOf([
    'jobTitle',
    'location',
    'major',
    'company',
    'department',
    'locationExtended',
    'companyIndustry',
  ]).isRequired,
  inputProps: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  prefillValue: PropTypes.string,
  onMenuToggle: PropTypes.func,
  criteria: PropTypes.object,
  initWithCriteria: PropTypes.bool,
  searchWithoutCall: PropTypes.any,
  clearOnSelect: PropTypes.bool,
  blurOnSelect: PropTypes.bool,
  onNormalizeData: PropTypes.func,
  isSearching: PropTypes.func,
  myRef: PropTypes.func,
  extended: PropTypes.bool,
  selectInputTextOnFocus: PropTypes.bool,
  autoFocus: PropTypes.bool,
  freeTextSupported: PropTypes.bool,
  redirectedLoc: PropTypes.bool,
  searchUnitedStates: PropTypes.bool,
  isIndexableOnly: PropTypes.bool,
  hidePlaceholder: PropTypes.bool,
  fetchPrefill: PropTypes.bool,
  surveyAutoAccept: PropTypes.bool,
  maxResults: PropTypes.number,
  clear: PropTypes.bool,
  stateOnlyLocations: PropTypes.bool,
  cityOnlyLocations: PropTypes.bool,
  staticSuggestions: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  // Select the first match when the user presses enter.
  // Notice: this prop has been deprecated in newer versions of react-bootstrap-typeahead.
  selectHintOnEnter: PropTypes.bool,
};

const defaultProps = {
  fromHomePage: false,
  onErrorCallback: () => {},
  onChange: () => {},
  onBlur: () => {},
  onKeyDown: () => {},
  onEnterKeyDown: () => {},
  onFocus: () => {},
  inputProps: {},
  prefillValue: '',
  onMenuToggle: () => {},
  criteria: {},
  initWithCriteria: false,
  clearOnSelect: false,
  blurOnSelect: false,
  onNormalizeData: () => {},
  isSearching: () => {},
  myRef: () => {},
  extended: false,
  selectInputTextOnFocus: true,
  autoFocus: false,
  freeTextSupported: false,
  redirectedLoc: false,
  searchUnitedStates: false,
  isIndexableOnly: false,
  hidePlaceholder: false,
  fetchPrefill: true,
  forceFromQuery: false,
  surveyAutoAccept: false,
  maxResults: 15,
  clear: false,
  stateOnlyLocations: false,
  cityOnlyLocations: false,
  staticSuggestions: [],
  className: '',
  selectHintOnEnter: true,
};

const defaultInputProps = {
  autoComplete: 'off',
  'data-hj-whitelist': true,
};

class AutosuggestInput extends Component {
  constructor(props) {
    super(props);
    const { category, inputProps, extended } = props;
    if (extended && category === 'location') {
      this.sourceHandler = this.getSourceByInputCategory('locationExtended').sourceHandler || null;
    } else if (!['department', 'companyIndustry'].includes(category) && category) {
      this.sourceHandler = this.getSourceByInputCategory(category).sourceHandler || null;
    }

    this.random = generateRandomKey();

    this.state = {
      input: {
        ...inputList[category],
        ...inputProps,
      },
      matches: [],
      departments: JSON.parse(JSON.stringify(props.departments)),
      isLoading: false,
      randomKey: Date.now(),
      // save received first object from search
      normalizeDataSet: {},
    };

    this._typeahead = React.createRef();
  }

  componentDidMount() {
    const { prefillValue, initWithCriteria, myRef } = this.props;
    const defaultValue = this.getDefaultValue();
    if (prefillValue) {
      // prefill with parent Component Default Value
      this.prefillInput(prefillValue);
    } else if (initWithCriteria && defaultValue) {
      // prefill with Input Category from Criteria
      this.prefillInput(defaultValue);
    }
    myRef(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { normalizeDataSet, isLoading } = this.state;
    const { onNormalizeData } = this.props;
    if (normalizeDataSet?.name !== prevState?.normalizeDataSet?.name) {
      onNormalizeData(normalizeDataSet);
    }
    if (isLoading !== prevState.isLoading) {
      const { isSearching } = this.props;
      isSearching(isLoading);
    }
  }

  componentWillUnmount() {
    const { onErrorCallback, myRef } = this.props;
    onErrorCallback({});
    myRef(undefined);
  }

  getDefaultValue = () => {
    const { category, criteria, initWithCriteria } = this.props;
    if (!initWithCriteria) {
      return '';
    }
    switch (category) {
      case 'location':
        if (criteria.state && !criteria.city && !criteria?.companyID) {
          return criteria.state;
        }
        if (criteria.city && criteria.stateAbbr && !criteria?.companyID) {
          return `${criteria.city}${criteria.stateAbbr ? `, ${criteria.stateAbbr}` : ''}`;
        }
        if (criteria.geoCity) {
          return `${criteria.geoCity}${criteria.geoStateCode ? `, ${criteria.geoStateCode}` : ''}`;
        }
        return '';
      case 'jobTitle':
        if (criteria.title) {
          return criteria.title;
        }
        return '';
      case 'company':
        if (criteria.companyNameCPS) {
          return criteria.companyNameCPS;
        }
        return '';
      case 'major':
        if (criteria.major) {
          return criteria.major;
        }
        return '';
      default:
        return '';
    }
  };

  prefillInput = (prefillValue) => {
    const { fetchPrefill } = this.props;

    this.setState({
      isLoading: true,
      prefillValueOrigin: this.state.prefillValue,
    });
    if (this.sourceHandler && prefillValue && fetchPrefill) {
      try {
        // trigger autosuggestion and select first option based on prefillValue prop
        this.sourceHandler(prefillValue).then((matches) => {
          if (this._typeahead && matches && matches.length > 0) {
            const { display_name, name, value, redirected_loc } = matches[0];
            this.setInputValue(display_name, name, value, redirected_loc);
            this.setState({
              matches,
              isLoading: false,
              normalizeDataSet: matches[0],
            });
            const instance = this._typeahead;
            const { initialItem } = instance.state;
            instance.setState({ activeIndex: matches.length, activeItem: initialItem });
          }
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  getSourceByInputCategory = (category) => {
    const { isIndexableOnly, stateOnlyLocations, cityOnlyLocations } = this.props;

    const options = {
      isIndexableOnly,
      stateOnlyLocations,
      cityOnlyLocations,
    };

    const {
      forTitle,
      forLocation,
      forMajor,
      forCompany,
      forCollege,
      forLocationExtended,
      forSkills,
      forCertifications,
      forLanguage,
    } = AutosuggestProxy;

    const categories = {
      jobTitle: forTitle(null, options),
      location: forLocation(null, options),
      locationExtended: forLocationExtended(),
      major: forMajor(null, options),
      company: forCompany(null, options),
      college: forCollege(),
      skills: forSkills(),
      certification: forCertifications(),
      language: forLanguage(),
    };

    return categories[category] || null;
  };

  handleInputChange = (value) => {
    const { category } = this.props;

    if (['department', 'companyIndustry'].includes(category)) {
      const filteredData = this.filteredItems(value);
      const filteredItems =
        filteredData && filteredData.length
          ? filteredData.map((filter) => {
              const { department, displayName } = filter;
              const item = {
                display_name: category === 'department' ? department : displayName,
                value: category === 'department' ? department : displayName,
                name: category === 'department' ? department : displayName,
              };
              return item;
            })
          : [];

      this.setState({ matches: filteredItems });
    } else {
      this.setInputValue(value);
    }
  };

  filteredItems = (displayValue) => {
    const { category, staticSuggestions } = this.props;
    if (category === 'department' && !staticSuggestions?.length) {
      const { departments } = this.state;
      return departments.filter(({ department }) => {
        // applying filter for the inserted text in search bar
        const itemData = department?.toUpperCase() || '';
        const textData = displayValue.toUpperCase();
        return itemData.indexOf(textData) > -1;
      });
    }
    return staticSuggestions.filter(({ displayName }) => {
      const matches = displayValue
        .toUpperCase()
        .split(' ')
        .map((word) => displayName.toUpperCase().includes(word));
      return matches.every((isMatch) => isMatch);
    });
  };

  setInputValue = (
    displayValue = '',
    value = '',
    valueObj = {},
    redirectedLoc = '',
    population,
    suggestionSelected = false
  ) => {
    const { onChange, onErrorCallback } = this.props;

    this.setState(
      (prevState) => ({
        input: {
          ...prevState.input,
          ...((value || displayValue === '') && { value }),
          ...((valueObj || displayValue === '') && { valueObj }),
          displayValue,
          suggestionSelected,
          redirectedLoc,
          population,
        },
      }),
      () => {
        const { input, isListOpen } = this.state;
        const { clearOnSelect, blurOnSelect } = this.props;
        input.isListOpen = isListOpen;
        onChange(input);
        if (onErrorCallback && displayValue === '') {
          // clear input error after updating Input
          onErrorCallback({
            errorMessage: '',
            inputCategory: input.category,
          });
        }
        if (suggestionSelected && clearOnSelect) {
          this._typeahead.clear();
        }
        if (suggestionSelected && blurOnSelect) {
          this._typeahead.blur();
        }
        if (suggestionSelected) {
          trackUserAutosuggestActivities(input);
        }
      }
    );
  };

  validateInput = (matches, query) => {
    const { input } = this.state;
    const { onErrorCallback } = this.props;
    let errorMessage = '';
    if (query !== '' && !matches.length) {
      errorMessage = input.errorMessage;
    }
    if (input.displayValue) {
      // set Error only if value exist inside Input
      onErrorCallback({
        errorMessage,
        inputCategory: input.category,
      });
    }
  };

  handleResultsChange = (selected) => {
    const { freeTextSupported, cityOnlyLocations } = this.props;

    const suggestion = selected[0];

    if (!suggestion) {
      return;
    }

    if (freeTextSupported && suggestion.major_indexinfo) {
      this.setInputValue(
        suggestion.major_indexinfo.major,
        suggestion.name,
        suggestion.value,
        suggestion.redirected_loc,
        suggestion.population,
        true
      );
      return;
    }

    if (
      cityOnlyLocations &&
      (suggestion?.displayFlagForSalaryPage !== undefined ||
        suggestion?.displayFlagForPartTimeJobsPage !== undefined)
    ) {
      const newValue = {
        ...(suggestion?.value || {}),
        displayFlagForSalaryPage: suggestion?.displayFlagForSalaryPage,
        displayFlagForPartTimeJobsPage: suggestion?.displayFlagForPartTimeJobsPage,
      };
      this.setInputValue(
        suggestion.display_name,
        suggestion.name,
        newValue,
        suggestion.redirected_loc,
        suggestion.population,
        true
      );
      return;
    }
    this.setInputValue(
      suggestion.display_name,
      suggestion.name,
      suggestion.value,
      suggestion.redirected_loc,
      suggestion.population,
      true
    );
  };

  handleSearch = (query) => {
    const {
      searchWithoutCall,
      updateSearchForParticularDepartment,
      updateSearchWithoutCallNumber,
      redirectedLoc,
      category,
      searchUnitedStates,
      onMatchesChange,
      onChange,
      criteria,
      surveyAutoAccept,
      freeTextSupported,
    } = this.props;

    const { input } = this.state;

    const match =
      category === 'location' &&
      searchUnitedStates &&
      UNITED_STATES.toLocaleLowerCase().includes(query.toLocaleLowerCase());

    this.setState({
      isLoading: true,
    });

    if (this.sourceHandler) {
      try {
        if (searchWithoutCall !== undefined) {
          updateSearchForParticularDepartment(true);
          updateSearchWithoutCallNumber(searchWithoutCall);
        }
        this.sourceHandler(query).then((matches) => {
          let allMatches =
            category === 'location' && redirectedLoc
              ? matches.filter((match) => match.redirected_loc)
              : matches;

          allMatches =
            category === 'location' && isUserOutsideUS(criteria)
              ? matches.filter(
                  (match) => !Object.values(REMOTE_CATEGORIES).includes(match.display_name)
                )
              : matches;

          const last = matches[matches.length - 1];

          const unitedStatesIndex = allMatches.findIndex(
            (currentMatch) => currentMatch.display_name === UNITED_STATES
          );

          if (match && unitedStatesIndex === -1) {
            allMatches.push({
              display_name: UNITED_STATES,
              extended: false,
              id: last ? last.id + 1 : 1,
              name: UNITED_STATES.toLocaleLowerCase(),
              original_name: UNITED_STATES,
              population: 0,
              redirected_loc: '',
              suggestion_name: UNITED_STATES,
              type: 'city',
            });
            allMatches = allMatches.sort((a, b) => a.display_name - b.display_name);
          }

          onMatchesChange && onMatchesChange(allMatches);

          if (surveyAutoAccept && !input.suggestionSelected && !!allMatches.length && onChange) {
            const { display_name, name, value, redirected_loc, population, zip_code } =
              allMatches[0];

            const object = {};

            display_name && (object['displayValue'] = display_name);
            name && (object['value'] = name);
            redirected_loc && (object['redirectedLoc'] = redirected_loc);
            population && (object['population'] = population);
            value && (object['valueObj'] = value);
            zip_code && (object['zipCode'] = zip_code);

            if (category) {
              object['name'] = category;
              object['category'] = category;
            }

            if (freeTextSupported && input.displayValue) {
              object['displayValue'] = input.displayValue;
            }

            onChange(object);
          }

          this.setState(
            {
              isLoading: false,
              matches: allMatches,
            },
            () => {
              if (matches.length > 0) {
                // set first normalize object only
                this.setState({
                  normalizeDataSet: allMatches[0],
                });
              }
            }
          );
          this.validateInput(allMatches, query);
        });
      } catch (e) {
        this.setState({
          isLoading: false,
        });
        console.error(e);
      }
    }
  };

  handleOnFocus = (event) => {
    const { onFocus, selectInputTextOnFocus } = this.props;
    const inputNode = event.target;
    if (selectInputTextOnFocus && inputNode && inputNode.value && inputNode.value.length) {
      inputNode.select();
      inputNode.setSelectionRange(0, inputNode.value.length);
    }
    onFocus(event);
  };

  __onKeyDown = (e) => {
    const { onKeyDown, onEnterKeyDown, freeTextSupported, selectHintOnEnter } = this.props;
    const { isListOpen, matches, input } = this.state;

    if (e.which === 13) {
      e.stopPropagation();
      e.preventDefault();
      if (!isListOpen || matches.length === 0) {
        onKeyDown(e);
      }

      if (freeTextSupported) {
        onEnterKeyDown(input, matches)(e);
        this._typeahead.blur();
        return;
      }

      /**
       * If selectHintOnEnter is enabled and there are no currenct active hints,
       * force-select the first item.
       * This is needed because typehead only selects the hint on enter if the begining
       * of its string matches the text in the input, making it possible to have hints
       * that don't get "auto-selected".
       */
      if (selectHintOnEnter && this._typeahead?.state.activeIndex === -1) {
        const initialItem = this._typeahead.state.initialItem;
        this._typeahead._handleMenuItemSelect(initialItem, e);
      }

      onEnterKeyDown(e);
    } else {
      onKeyDown(e);
    }
  };

  __onMenuToggle = (show) => {
    const { onMenuToggle } = this.props;
    this.setState(
      () => ({
        isListOpen: show,
      }),
      () => {
        onMenuToggle(show);
      }
    );
  };

  renderMenuItem = (option, props) => {
    const { category } = this.props;
    return (
      <Highlighter search={props.text}>
        {category === 'jobTitle' ? option.name : option.display_name}
      </Highlighter>
    );
  };

  render() {
    const {
      inputProps,
      category,
      onBlur,
      prefillValue,
      fromHomePage,
      autoFocus,
      hidePlaceholder,
      maxResults,
      clear,
      className,
      selectHintOnEnter,
    } = this.props;
    const { input, isLoading, matches, randomKey } = this.state;
    const { handleInputChange, handleResultsChange, handleSearch, handleOnFocus } = this;
    const { placeholder } = input;

    if (clear) {
      if (this._typeahead.current !== null) {
        this._typeahead.clear();
      }
    }

    return (
      <AsyncTypeahead
        id={(inputProps.id || '') + '_AsyncTypeahead_' || randomKey}
        className={classNames(
          fromHomePage
            ? css['autosuggestion-home']
            : classNames(className, {
                [css.hideSuggestion]: !matches.length,
              }),
          css['async-root']
        )}
        autoFocus={autoFocus && !prefillValue}
        maxHeight="auto"
        ref={(typeahead) => (this._typeahead = typeahead)}
        placeholder={!hidePlaceholder ? placeholder : ''}
        defaultInputValue={prefillValue || this.getDefaultValue()}
        onChange={handleResultsChange} // when selecting option
        onSearch={handleSearch} // when search is executed
        onInputChange={handleInputChange} // when input value is changed
        onBlur={onBlur}
        onFocus={handleOnFocus}
        onKeyDown={this.__onKeyDown}
        maxResults={maxResults}
        minLength={1}
        promptText={false}
        filterBy={(option) => option}
        paginate={false}
        useCache={false}
        shouldSelect={(shouldSelect, e) => {
          return e.keyCode === 13 || shouldSelect;
        }}
        isLoading={isLoading}
        delay={200}
        options={matches}
        labelKey="display_name"
        emptyLabel={false}
        searchText={<span className="opensSansBlack">Searching...</span>}
        renderMenuItemChildren={this.renderMenuItem}
        inputProps={{
          name: category,
          type: 'text',
          autoComplete: 'off',
          ...inputProps,
          ...defaultInputProps,
          id: inputProps.id,
        }}
        onMenuToggle={this.__onMenuToggle}
        selectHintOnEnter={selectHintOnEnter}
      />
    );
  }
}

AutosuggestInput.defaultProps = defaultProps;
AutosuggestInput.propTypes = propTypes;

const mapStateToProps = ({
  locals: { criteria },
  searchFilter: { searchWithoutCallNumber, departmentData },
}) => ({
  criteria,
  searchWithoutCallNumber,
  departments: departmentData,
});

const mapDispatchToProps = {
  updateSearchWithoutCallNumber,
  updateSearchForParticularDepartment,
};

export default connect(mapStateToProps, mapDispatchToProps)(AutosuggestInput);
